// --- React & Redux
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Drawer, Spin, Alert, Statistic, Row, Col, Card, Table, Input, Space, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Button from '@dtci/spellbook/Button';
// --- Hooks & Actions
import { useProductGateActions } from 'datastage/store/productGate/useProductGate';
import { useStaleTitlesData } from '../../hooks/useStaleTitlesData';
// --- Styles
import './styles.scss';

/**
 * Stale Titles Drawer - displays stats and allows CSV export
 */
export const StaleTitlesDrawer = () => {
    const { isStaleTitlesOpen } = useSelector((state) => state.productGate);
    const { updateProductGate } = useProductGateActions();
    const { fetchStaleTitlesStats, downloadStaleTitlesCsv, downloadStaleTitlesInRlCsv, downloadStaleTitlesByReasonInRlCsv } = useStaleTitlesData();

    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingRl, setDownloadingRl] = useState(false);
    const [downloadingReason, setDownloadingReason] = useState(null);
    const [downloadingReasonRl, setDownloadingReasonRl] = useState(null);
    const [searchText, setSearchText] = useState('');
    useEffect(() => {
        if (isStaleTitlesOpen) {
            loadStats();
        }
    }, [isStaleTitlesOpen]);

    const loadStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchStaleTitlesStats();
            setStats(data);
        } catch (err) {
            setError(err.message || 'Failed to load stale titles statistics');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        updateProductGate({
            isStaleTitlesOpen: false,
        });
        setStats(null);
        setError(null);
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            await downloadStaleTitlesCsv();
        } catch (err) {
            setError(err.message || 'Failed to download CSV');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadRl = async () => {
        setDownloadingRl(true);
        try {
            await downloadStaleTitlesInRlCsv();
        } catch (err) {
            setError(err.message || 'Failed to download CSV');
        } finally {
            setDownloadingRl(false);
        }
    };

    const handleDownloadByReason = async (failureReason) => {
        setDownloadingReason(failureReason);
        try {
            await downloadStaleTitlesCsv(failureReason);
        } catch (err) {
            setError(err.message || 'Failed to download CSV');
        } finally {
            setDownloadingReason(null);
        }
    };

    const handleDownloadByReasonRl = async (failureReason) => {
        setDownloadingReasonRl(failureReason);
        try {
            await downloadStaleTitlesByReasonInRlCsv(failureReason);
        } catch (err) {
            setError(err.message || 'Failed to download CSV');
        } finally {
            setDownloadingReasonRl(null);
        }
    };

    const filteredReasons = stats?.topFailureReasons?.filter((item) =>
        item.reason.toLowerCase().includes(searchText.toLowerCase())
    ) || [];
    const columns = [
        {
            title: 'Failure Reason',
            dataIndex: 'reason',
            key: 'reason',
            ellipsis: true,
            render: (text) => (
                <span style={{ fontSize: '14px', color: '#262626' }}>{text}</span>
            ),
        },
        {
            title: 'Total',
            dataIndex: 'count',
            key: 'count',
            width: 120,
            align: 'right',
            sorter: (a, b) => a.count - b.count,
            defaultSortOrder: 'descend',
            render: (count) => (
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>
                    {count.toLocaleString()}
                </span>
            ),
        },
        {
            title: 'In RL',
            dataIndex: 'rlCount',
            key: 'rlCount',
            width: 120,
            align: 'right',
            sorter: (a, b) => (a.rlCount || 0) - (b.rlCount || 0),
            render: (rlCount) => (
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#fa8c16' }}>
                    {(rlCount || 0).toLocaleString()}
                </span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 220,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        iconType={downloadingReason === record.reason ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-download'}
                        styleType="link"
                        sizeType="small"
                        onClick={() => handleDownloadByReason(record.reason)}
                        disabled={downloadingReason === record.reason}
                        title="Download all titles with this failure reason"
                    >
                        CSV (All)
                    </Button>
                    <Button
                        iconType={downloadingReasonRl === record.reason ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-download'}
                        styleType="link"
                        sizeType="small"
                        onClick={() => handleDownloadByReasonRl(record.reason)}
                        disabled={downloadingReasonRl === record.reason || (record.rlCount || 0) === 0}
                        title="Download only titles in RL with this failure reason"
                    >
                        CSV (RL Only)
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Drawer
            className="stale-titles-drawer"
            title={
                <span style={{ fontSize: '24px', fontWeight: 600, color: '#262626' }}>
                    Health Monitoring - Stale Titles
                </span>
            }
            width={1200}
            placement="right"
            open={isStaleTitlesOpen}
            onClose={handleClose}
            bodyStyle={{ paddingBottom: 80 }}
        >
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" tip="Loading statistics..." />
                </div>
            )}

            {(downloading || downloadingRl) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.45)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                    borderRadius: '4px'
                }}>
                    <Spin size="large" />
                    <div style={{
                        color: '#fff',
                        fontSize: '16px',
                        marginTop: 16,
                        fontWeight: 500
                    }}>
                        Generating CSV export...
                    </div>
                    <div style={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontSize: '14px',
                        marginTop: 8
                    }}>
                        This may take a moment for large datasets
                    </div>
                </div>
            )}

            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{ marginBottom: 20 }}
                />
            )}

            {!loading && stats && (
                <>
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={12}>
                            <Card>
                                <Statistic
                                    title="Total STALE Titles"
                                    value={stats.totalStale}
                                    valueStyle={{ color: '#cf1322' }}
                                    suffix="titles"
                                />
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card>
                                <Statistic
                                    title="STALE Titles in RL"
                                    value={stats.totalStaleInRL || 0}
                                    valueStyle={{ color: '#fa8c16' }}
                                    suffix="titles"
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={24}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '16px',
                                padding: '0 0 16px 0'
                            }}>
                                <Button
                                    iconType="fa-solid fa-arrow-down-to-line"
                                    labelText="Download All Stale Titles (CSV)"
                                    styleType="link"
                                    onClick={handleDownload}
                                    disabled={stats.totalStale === 0 || downloading}
                                >
                                    Download All Stale Titles (CSV)
                                </Button>
                                <Button
                                    iconType="fa-solid fa-arrow-down-to-line"
                                    labelText="Download All Stale Titles in RL (CSV)"
                                    styleType="link"
                                    onClick={handleDownloadRl}
                                    disabled={(stats.totalStaleInRL || 0) === 0 || downloadingRl}
                                >
                                    Download All Stale Titles in RL (CSV)
                                </Button>
                            </div>
                            {stats.totalStale === 0 && (
                                <p style={{ marginBottom: 16, color: '#8c8c8c', textAlign: 'center' }}>
                                    No stale titles to export
                                </p>
                            )}
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={24}>
                            <Card
                                title={
                                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <span>Top 50 Failure Reasons</span>
                                        <Input
                                            placeholder="Search failure reasons..."
                                            prefix={<SearchOutlined />}
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ width: 300 }}
                                            allowClear
                                        />
                                    </Space>
                                }
                            >
                                {stats.topFailureReasons && stats.topFailureReasons.length > 0 ? (
                                    <Table
                                        columns={columns}
                                        dataSource={filteredReasons}
                                        rowKey={(record) => record.reason}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total ${total} failure reasons`,
                                            pageSizeOptions: ['10', '20', '50'],
                                        }}
                                        size="middle"
                                        bordered
                                        className="stale-titles-table"
                                        scroll={{ y: 400 }}
                                        locale={{
                                            emptyText: searchText
                                                ? `No failure reasons matching "${searchText}"`
                                                : 'No failure reasons available'
                                        }}
                                    />
                                ) : (
                                    <p style={{ textAlign: 'center', color: '#8c8c8c', padding: '20px 0' }}>
                                        No failure reasons available
                                    </p>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </Drawer>
    );
};
